/** @jsx jsx */
// eslint-disable-next-line jira/restricted/react-component-props
import React, { Component, forwardRef, type ComponentProps, createRef } from 'react';
import { cssMap, cx, jsx } from '@compiled/react';
import debounce from 'lodash/debounce';
import flatMap from 'lodash/flatMap';
import type { Disposable } from 'relay-runtime';
import type { UseMutationConfig } from 'react-relay';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { Field } from '@atlaskit/form';
import {
	CreatableSelect,
	components,
	type OptionProps,
	type StylesConfig,
	type MultiValueGenericProps,
	type MultiValueRemoveProps,
} from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import type { PopupComponentProps, TriggerProps } from '@atlaskit/popup';
import { layers as themeLayers } from '@atlaskit/theme/constants';
import { layers } from '@atlassian/jira-common-styles/src/main.tsx';
import { JiraInlineDialog as InlineDialog } from '@atlassian/jira-inline-dialog/src/ui/jira-inline-dialog.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import { memoizeGetAutoGeneratedColorForLabels } from '@atlassian/jira-option-color-picker/src/option-color-generator/index.tsx';
import { OptionTag } from '@atlassian/jira-option-color-picker/src/option-tag/index.tsx';
import { getColorMetadata } from '@atlassian/jira-option-color-picker/src/color-picker/utils/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { LabelsColorPickerForm } from '@atlassian/jira-option-color-picker/src/labels-color-picker/index.tsx';
import type { ColorOptionData } from '@atlassian/jira-option-color-picker/src/labels-color-picker/types.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import type { ColoredLabel } from '@atlassian/jira-issue-shared-types/src/common/types/labels-type.tsx';
import type {
	view_labelsUpdateColorMutation,
	view_labelsUpdateColorMutation$data as MutationData,
	JiraOptionColorInput,
} from '@atlassian/jira-relay/src/__generated__/view_labelsUpdateColorMutation.graphql';
import { fireTrackAnalyticsDeferred } from '@atlassian/jira-product-analytics-bridge';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { GREY_META } from '@atlassian/jira-option-color-picker/src/color-picker/constants.tsx';
import messages from './messages.tsx';
import { ColoredLabel as JiraColoredLabel } from './colored-label/index.tsx';
import type { LabelOption } from './model/index.tsx';
import { labelsService, validationErrorMessage } from './services/index.tsx';

const DEBOUNCE_TIME = 500;

export type Props = {
	isRequired: boolean;
	/* This will show a red border and warning icon when true. This won't render any validation message as consumers are
	expected to render that themselves outside of the component. Note this does not affect the internal validation state
	of the component. e.g. if `isInvalid=false` and the picker failed to fetch options, then an error state will still
	be shown. */
	isInvalid: boolean;
} & JSX.LibraryManagedAttributes<typeof CreatableSelect, ComponentProps<typeof CreatableSelect>> & {
		label: string;
		value?: ReadonlyArray<string>;
		isDisabled?: boolean;
		noPortal: boolean;
		// @deprecated baseUrl is always '', remove it from your packages: go/baseurl-cleanup-jfe
		baseUrl?: string;
		customFieldId?: string;
		/** Focus the control when it is mounted */
		autoFocus?: boolean;
		initialOptions?: LabelOption[];
		enableAnimation?: boolean;
		onChange: (arg1: string[] | ColoredLabel[], arg2?: UIAnalyticsEvent) => void;
		noOptionsErrorMessage?: string;
		isDefaultValidationState?: boolean;
		intl: IntlShape;
		isColored?: boolean;
		isProjectAdmin?: boolean;
		isGlobalAdmin?: boolean;
		commit?: (config: UseMutationConfig<view_labelsUpdateColorMutation>) => Disposable;
		fieldId?: string;
		projectAriId?: string;
		lastFocusedKey?: React.MutableRefObject<{ key: string; index: number; type: string } | null>;
		analyticsEvent?: UIAnalyticsEvent;
	};

export type State = {
	labels: string[] | ColoredLabel[];
	options: LabelOption[];
	allSuggestions: LabelOption[];
	errorType: 'validation' | 'general' | undefined;
	isLoading: boolean;
	showColorPickerPopupWhenEdited: boolean;
	selectedLabelForEdit: LabelOption | undefined;
	hasLabelColorChanged: boolean;
	isFocusingOptions: boolean;
	isMenuOpen: boolean;
};

// This functions double checks for labels with spaces or commas.
// If such is detected then the string is split into two separate labels.
// f.in. ["foo bar", "baz"] => ["foo", "bar", "baz"]
export const transformLabels = (selectedOptions: string[]): string[] =>
	flatMap(selectedOptions, (str: string) => str.trim().split(/[\s,]+/)).filter(
		(label) => label.length > 0,
	);

export const transformLabelsNew = (selectedOptions: ColoredLabel[]): ColoredLabel[] => {
	return selectedOptions.flatMap((option) => {
		return option.name
			.trim()
			.split(/[\s,]+/)
			.filter((label) => label.length > 0)
			.map((splitName) => {
				return {
					name: splitName,
					...(option.color ? { color: option.color } : {}),
					...(option.__isNew__ ? { __isNew__: option.__isNew__ } : {}),
				};
			});
	});
};

const multiValueRemoveStyles = cssMap({
	root: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div > span > svg': {
			transform: 'scale(1.5)',
			fill: 'none',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div > span > svg > g > path': {
			d: 'path("m 2 10 l 4 -4 m 4 -4 L 6 6 m 0 0 L 2 2 m 4 4 l 4 4")',
			transform: 'translate(5px, 6px)',
			stroke: token('color.text'),
			strokeWidth: '1.5',
		},
	},
});

const multiValueStyles = cssMap({
	root: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div > div > div': {
			paddingLeft: token('space.0'),
			paddingRight: token('space.0'),
		},
	},
	isVisualRefreshDisabled: {
		borderRadius: token('border.radius.100'),
		backgroundColor: 'transparent',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div': {
			paddingLeft: token('space.050'),
			paddingRight: token('space.0'),
			paddingTop: token('space.0'),
			paddingBottom: token('space.0'),
		},
	},
});

const multiValueLabelStyles = cssMap({
	root: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div > div': {
			borderColor: 'transparent',
		},
	},
});

// eslint-disable-next-line jira/react/no-class-components
export class LabelsPicker extends Component<Props, State> {
	static defaultProps = {
		isInvalid: false,
		noPortal: false,
		autoFocus: false,
	};

	state = {
		labels: [],
		options: [],
		allSuggestions: [],
		errorType: undefined,
		isLoading: false,
		showColorPickerPopupWhenEdited: false,
		selectedLabelForEdit: undefined,
		hasLabelColorChanged: false,
		isFocusingOptions: false,
		isMenuOpen: false,
	};

	componentDidMount() {
		this.setState({ isLoading: true });
		this.fetchOptions();
	}

	componentDidUpdate(prevProps: Props) {
		// automatically refetch options when these props change
		if (
			prevProps.baseUrl !== this.props.baseUrl ||
			prevProps.customFieldId !== this.props.customFieldId
		) {
			this.setState({ isLoading: true });
			this.fetchOptions();
		}
	}

	inputRef: React.RefObject<HTMLInputElement> = createRef();

	loadingMessage = () => this.props.intl.formatMessage(messages.loading);

	noOptionsMessage = () => {
		const { errorType } = this.state;
		const {
			intl: { formatMessage },
			noOptionsErrorMessage,
		} = this.props;
		const message =
			errorType === 'general' && noOptionsErrorMessage
				? noOptionsErrorMessage
				: formatMessage(messages.noOptions);

		return message;
	};

	onSelected = (
		selection: LabelOption[] | LabelOption | null,
		actionMeta: {
			action: string;
		},
		analyticsEvent?: UIAnalyticsEvent,
	) => {
		if (Array.isArray(selection)) {
			this.onChange(selection, analyticsEvent);
		} else {
			selection && selection.value
				? this.onChange([selection], analyticsEvent)
				: this.onChange([], analyticsEvent);
		}
	};

	onChange(selectedOption: LabelOption[], analyticsEvent?: UIAnalyticsEvent) {
		if (
			expVal(
				'adding-color-to-labels-field-experiment-milestone2',
				'isColorfulNewLabelsEnabled',
				false,
			)
		) {
			const labels = selectedOption.map((option) => {
				return {
					name: option.label,
					...(option.color ? { color: option.color } : {}),
					...(option.__isNew__ ? { __isNew__: option.__isNew__ } : {}),
				};
			});

			this.setState({
				labels,
				isMenuOpen: false,
			});
			this.props.onChange(transformLabelsNew(labels), analyticsEvent);
		} else {
			const labels = selectedOption.map((option) => option.label);
			this.setState({
				labels,
			});
			this.props.onChange(transformLabels(labels), analyticsEvent);
		}
	}

	getColoredLabels(value: string[] | ColoredLabel[]) {
		const options = this.state.allSuggestions;

		const labels = this.state.labels;

		const coloredLabels: ColoredLabel[] = value.map((labelOption: string | ColoredLabel) => {
			if (typeof labelOption !== 'string') {
				const matchedLabel: LabelOption | undefined = options.find(
					(option: LabelOption) => option.label === labelOption.name,
				);

				if (matchedLabel) {
					// @ts-expect-error - ts2339 - Property 'color' does not exist on type 'never'.
					return { ...labelOption, ...(matchedLabel.color ? { color: matchedLabel.color } : {}) };
				}

				const labelOptionMatched = labels.find((lab: string | ColoredLabel) => {
					if (typeof lab !== 'string') {
						if (lab.name === labelOption.name) {
							return lab.color ? lab.color : null;
						}
						return null;
					}
					return null;
				});
				const labelOptionColor =
					// @ts-expect-error - ts2339 - Property 'color' does not exist on type 'never'.
					labelOption.color || (labelOptionMatched?.color ? labelOptionMatched?.color : null);

				return {
					...labelOption,
					...(labelOptionColor ? { color: labelOptionColor } : {}),
				};
			}
			const matchedLabel: LabelOption | undefined = options.find(
				(option: LabelOption) => option.label === labelOption,
			);

			if (matchedLabel) {
				// @ts-expect-error - ts2339 - Property 'color' does not exist on type 'never'.
				return { name: labelOption, ...(matchedLabel.color ? { color: matchedLabel.color } : {}) };
			}

			const labelOptionMatched = labels.find((lab: string | ColoredLabel) => {
				if (typeof lab !== 'string') {
					if (lab.name === labelOption) {
						return lab.color ? lab.color : null;
					}
					return null;
				}
				return null;
			});

			return {
				name: labelOption,
				// @ts-expect-error - ts2339 - Property 'color' does not exist on type 'never'.
				...(labelOptionMatched?.color ? { color: labelOptionMatched?.color } : {}),
			};
		});

		return coloredLabels;
	}

	onCreateOption = (value: string) => {
		const labels = this.props.value || this.state.labels;
		if (
			expVal(
				'adding-color-to-labels-field-experiment-milestone2',
				'isColorfulNewLabelsEnabled',
				false,
			)
		) {
			// @ts-expect-error - ts2345 - Argument of type 'readonly string[]' is not assignable to parameter of type 'string[] | ColoredLabel[]'.
			const coloredLabels = this.getColoredLabels(labels);
			const newLabels: ColoredLabel[] = [
				...coloredLabels,
				{ name: value, __isNew__: true, color: GREY_META.name },
			];

			this.setState({
				selectedLabelForEdit: { label: value, value, __isNew__: true },
				labels: newLabels,
			});
			this.props.onChange(transformLabelsNew(newLabels));

			fireTrackAnalyticsDeferred(
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				this.props.analyticsEvent as UIAnalyticsEvent,
				'newLabel created',
				{
					label: value,
					fieldId: this.props.fieldId,
				},
			);
		} else {
			const newLabels = [...labels, value];
			this.setState({
				labels: newLabels,
			});
			this.props.onChange(transformLabels(newLabels));
		}
	};

	debouncedLoadOptions = debounce((filter: string) => {
		this.fetchOptions(filter);
	}, DEBOUNCE_TIME);

	fetchOptions(search = '') {
		labelsService(search, this.props.customFieldId)
			.then((result: LabelOption[]) => {
				if (
					expVal(
						'adding-color-to-labels-field-experiment-milestone2',
						'isColorfulNewLabelsEnabled',
						false,
					)
				) {
					this.setState({
						isLoading: false,
						options: [...(this.props.initialOptions || []), ...result],
						allSuggestions:
							this.state.allSuggestions.length === 0 || this.state.hasLabelColorChanged
								? [...(this.props.initialOptions || []), ...result]
								: this.state.allSuggestions,
						hasLabelColorChanged: false,
					});
				} else {
					this.setState({
						isLoading: false,
						options: [...(this.props.initialOptions || []), ...result],
					});
				}
				return result;
			})
			.catch((err) => {
				if (err.message === validationErrorMessage) {
					this.setState({
						errorType: 'validation',
						isLoading: false,
						options: [],
					});
				} else {
					this.setState({
						errorType: 'general',
						isLoading: false,
						options: [],
					});
				}
			});
	}

	formatCreateLabel = (value: string) => {
		const {
			intl: { formatMessage },
		} = this.props;
		return `${value} (${formatMessage(messages.new)})`;
	};

	onInputChange = (value: string) => {
		this.setState({ isLoading: true, errorType: undefined });
		expVal(
			'adding-color-to-labels-field-experiment-milestone2',
			'isColorfulNewLabelsEnabled',
			false,
		) && this.setState({ isMenuOpen: true });
		this.debouncedLoadOptions(value);
	};

	formatOptionLabel = (option: LabelOption) => {
		const {
			intl: { formatMessage },
		} = this.props;

		const optionLabel: string = option.label;
		const createNewLabelString = `(${formatMessage(messages.new)})`;

		if (typeof optionLabel === 'string' && optionLabel.endsWith(createNewLabelString)) {
			const optionLabelWithoutNewLabel = optionLabel.slice(0, -createNewLabelString.length).trim();

			return (
				<OptionTag
					name={optionLabel}
					color={memoizeGetAutoGeneratedColorForLabels(optionLabelWithoutNewLabel)}
				/>
			);
		}

		return (
			<OptionTag name={optionLabel} color={memoizeGetAutoGeneratedColorForLabels(optionLabel)} />
		);
	};

	onEditOption = (option: LabelOption) => {
		this.setState({ selectedLabelForEdit: option, showColorPickerPopupWhenEdited: true });
	};

	resetLabelsColorPickerState = () => {
		this.setState({
			selectedLabelForEdit: undefined,
			showColorPickerPopupWhenEdited: false,
		});
	};

	submitLabelsUpdatedColor(param: ColorOptionData) {
		const selectedLabelForEdit: LabelOption | undefined = this.state.selectedLabelForEdit;
		if (!selectedLabelForEdit) {
			return;
		}

		this.props.commit?.({
			variables: {
				input: {
					projectId: this.props.projectAriId || '',
					labelColorUpdate: [
						{
							fieldId: this.props.fieldId || '',
							label: param.name,
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							color: param.color as JiraOptionColorInput,
						},
					],
				},
			},
			optimisticResponse: {
				jira: {
					bulkUpdateLabelColor: {
						success: true,
						errors: null,
					},
				},
			},
			onCompleted: (mutationData: MutationData) => {
				if (mutationData?.jira?.bulkUpdateLabelColor?.success) {
					this.setState({ hasLabelColorChanged: true });
					this.fetchOptions();
					fireTrackAnalyticsDeferred(
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						this.props.analyticsEvent as UIAnalyticsEvent,
						'labelColor updated',
						{
							label: param,
						},
					);
				} else {
					const errorMessage =
						mutationData?.jira?.bulkUpdateLabelColor?.errors?.[0]?.message ?? undefined;
					fireErrorAnalytics({
						meta: {
							id: 'labelColorUpdate',
							packageName: 'jiraLabelPicker',
							teamName: 'gryffindor',
						},
						attributes: {
							label: param,
							errorMessage,
						},
						sendToPrivacyUnsafeSplunk: true,
					});
				}
			},
			onError(error: Error) {
				fireErrorAnalytics({
					meta: {
						id: 'labelColorUpdate',
						packageName: 'jiraLabelPicker',
						teamName: 'gryffindor',
					},
					attributes: {
						label: param,
					},
					error,
					sendToPrivacyUnsafeSplunk: true,
				});
			},
		});
	}

	handleFocus = () => {
		this.setState({ isMenuOpen: !this.state.isMenuOpen });
		if (this.props.lastFocusedKey) {
			this.props.lastFocusedKey.current = { key: 'input', index: 0, type: 'input' };
		}
	};

	handleBlur = (e: React.FocusEvent<HTMLElement>) => {
		const button = globalThis.document
			.querySelector(
				'div[role="group"] > button[type="button"] [data-testid="option-color-picker.option-tag.field-tag-wrapper"]',
			)
			?.closest('button');

		if (e.relatedTarget === button) {
			this.setState({ isFocusingOptions: true });
		} else {
			this.setState({ isMenuOpen: false, isFocusingOptions: false });
		}
	};

	handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
		if (this.state.isMenuOpen === false && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
			this.setState({ isMenuOpen: true });
		}

		if (e.key === 'Enter') {
			e.preventDefault();
		}

		if (e.key === 'Escape') {
			this.setState({ isMenuOpen: false });
		}

		const menu = e.currentTarget.querySelector('[role="dialog"]');

		if (!menu || !['Tab', 'ArrowDown', 'ArrowUp'].includes(e.key)) return;

		const input = e.currentTarget.querySelector('input');

		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const buttons = Array.from(
			menu.querySelectorAll('button:not([disabled]), [role="group"] button:not([disabled])'),
		) as HTMLElement[];

		const focusableElements = [input, ...buttons];

		const currentFocusIndex = focusableElements.findIndex(
			(el) => el === globalThis.document.activeElement,
		);

		const direction = e.key === 'ArrowUp' || (e.key === 'Tab' && e.shiftKey) ? -1 : 1;
		const nextIndex =
			(currentFocusIndex + direction + focusableElements.length) % focusableElements.length;

		e.preventDefault();

		requestAnimationFrame(() => {
			focusableElements[nextIndex]?.focus();
		});
	};

	render() {
		const {
			label,
			isRequired,
			value,
			noPortal,
			intl: { formatMessage },
			isInvalid,
			isColored,
			isDefaultValidationState,
			...rest
		} = this.props;

		const { labels, errorType, options, isLoading, showColorPickerPopupWhenEdited } = this.state;
		const selectedLabelForEdit: LabelOption | undefined = this.state.selectedLabelForEdit;

		const coloredLabels = expVal(
			'adding-color-to-labels-field-experiment-milestone2',
			'isColorfulNewLabelsEnabled',
			false,
		)
			? // @ts-expect-error - ts2345 - The type 'readonly string[]' is 'readonly' and cannot be assigned to the mutable type 'ColoredLabel[]'.
				this.getColoredLabels(value || labels)
			: [];

		// @ts-expect-error - ts2322 -  Type '{ label: string | ColoredLabel; value: string | ColoredLabel; }' is not assignable to type 'LabelOption'.
		const pickerValues: LabelOption[] = (value || labels).map((lab: string | ColoredLabel) => {
			if (
				expVal(
					'adding-color-to-labels-field-experiment-milestone2',
					'isColorfulNewLabelsEnabled',
					false,
				)
			) {
				const matchedItem = coloredLabels.find((item) => {
					if (typeof lab !== 'string') {
						return item.name === lab.name;
					}

					return item.name === lab;
				});

				if (typeof lab !== 'string') {
					const labelColor = (matchedItem && matchedItem.color) || lab.color;

					return {
						label: matchedItem ? matchedItem.name : lab.name,
						value: matchedItem ? matchedItem.name : lab.name,
						...(labelColor ? { color: labelColor } : {}),
					};
				}
				return {
					label: matchedItem ? matchedItem.name : lab,
					value: matchedItem ? matchedItem.name : lab,
					...(matchedItem && matchedItem.color ? { color: matchedItem.color } : {}),
				};
			}

			return {
				label: lab,
				value: lab,
			};
		});

		const isInlineDialogOpen = this.props.noOptionsErrorMessage
			? Boolean(errorType === 'validation')
			: Boolean(errorType);

		const validationState =
			!isDefaultValidationState && (isInvalid || errorType) ? 'error' : 'default';

		const selectStyles: StylesConfig<LabelOption, true> = {
			menuPortal: (base) => ({ ...base, zIndex: layers.modal }),
		};

		const labelsValueStyles: StylesConfig<LabelOption, true> = {
			multiValue: (base, { data }) => {
				const dataLabel: string = data.label;

				const { backgroundColor: backgroundColorMilestone1 } = getColorMetadata(
					memoizeGetAutoGeneratedColorForLabels(dataLabel),
				);

				const isNewLabel =
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					selectedLabelForEdit && (selectedLabelForEdit as LabelOption).label === data.label
						? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							(selectedLabelForEdit as LabelOption).__isNew__
						: data.__isNew__;

				const newLabelColor = data.color ?? GREY_META.name;
				const oldLabelColor = data.color ?? memoizeGetAutoGeneratedColorForLabels(dataLabel);

				const { backgroundColor: backgroundColorMilestone2 } = getColorMetadata(
					isNewLabel ? newLabelColor : oldLabelColor,
				);

				const backgroundColor = expVal(
					'adding-color-to-labels-field-experiment-milestone2',
					'isColorfulNewLabelsEnabled',
					false,
				)
					? backgroundColorMilestone2
					: backgroundColorMilestone1;

				return {
					...base,
					border: `1px solid ${backgroundColor}`,
					'& > div > div > div': {
						paddingLeft: 0,
						paddingRight: 0,
					},
					...(!isVisualRefreshEnabled()
						? {
								borderRadius: token('border.radius.100'),
								backgroundColor: 'transparent',
								'& > div': {
									paddingLeft: token('space.050'),
									paddingRight: 0,
									paddingTop: 0,
									paddingBottom: 0,
								},
							}
						: {}),
				};
			},
			multiValueLabel: (base) => ({
				...base,
				'& > div > div': {
					borderColor: 'transparent',
				},
			}),
		};
		const isVisualRefreshDisabled = !isVisualRefreshEnabled();

		const labelsRemoveIconStyles: StylesConfig<LabelOption, true> = {
			multiValueRemove: (base) => ({
				...base,
				'& > div > span > svg': {
					transform: 'scale(1.5)',
					fill: 'none',
				},
				'& > div > span > svg > g > path': {
					d: 'path("m 2 10 l 4 -4 m 4 -4 L 6 6 m 0 0 L 2 2 m 4 4 l 4 4")',
					transform: 'translate(5px, 6px)',
					stroke: token('color.text'),
					strokeWidth: '1.5',
				},
			}),
		};

		const styles = () => {
			if (
				isColored ||
				expVal(
					'adding-color-to-labels-field-experiment-milestone2',
					'isColorfulNewLabelsEnabled',
					false,
				)
			) {
				if (!isVisualRefreshEnabled()) {
					return { ...selectStyles, ...labelsValueStyles, ...labelsRemoveIconStyles };
				}
				return { ...selectStyles, ...labelsValueStyles };
			}

			return selectStyles;
		};

		const handleCancel = () => {
			this.resetLabelsColorPickerState();
		};

		const handleSubmit = (param: ColorOptionData) => {
			this.resetLabelsColorPickerState();
			this.submitLabelsUpdatedColor(param);
		};

		const popupContent = () => {
			const isNewLabel =
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				(selectedLabelForEdit && (selectedLabelForEdit as LabelOption)?.__isNew__) || false;

			const newLabelColor =
				(selectedLabelForEdit && // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					(selectedLabelForEdit as LabelOption)?.color) ||
				'';

			const oldLabelColor =
				(selectedLabelForEdit && // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					(selectedLabelForEdit as LabelOption)?.color) ||
				memoizeGetAutoGeneratedColorForLabels(
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					selectedLabelForEdit && (selectedLabelForEdit as LabelOption)?.label?.toString(),
				);

			return (
				<LabelsColorPickerForm
					onCancel={handleCancel}
					onSubmit={handleSubmit}
					initialData={{
						name: selectedLabelForEdit
							? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
								(selectedLabelForEdit as LabelOption).label?.toString()
							: '',
						color: isNewLabel ? newLabelColor : oldLabelColor,
					}}
				/>
			);
		};

		const styleComponents = () => {
			if (
				expVal(
					'adding-color-to-labels-field-experiment-milestone2',
					'isColorfulNewLabelsEnabled',
					false,
				) ||
				isColored
			) {
				return {
					MultiValueContainer: (props: MultiValueGenericProps<LabelOption>) => (
						<components.MultiValueContainer
							{...props}
							xcss={cx(
								multiValueStyles.root,
								isVisualRefreshDisabled && multiValueStyles.isVisualRefreshDisabled,
							)}
						/>
					),
					MultiValueLabel: (props: MultiValueGenericProps<LabelOption>) => (
						<components.MultiValueLabel {...props} xcss={multiValueLabelStyles.root} />
					),
					MultiValueRemove: (props: MultiValueRemoveProps<LabelOption>) => (
						<components.MultiValueRemove
							{...props}
							xcss={isVisualRefreshDisabled && multiValueRemoveStyles.root}
						/>
					),
				};
			}
		};

		const labelsField = (
			<Field name="platform-ui-pickers-label" label={label} isRequired={isRequired}>
				{({ fieldProps }) => (
					<>
						<InlineDialog
							content={
								errorType === 'validation'
									? formatMessage(messages.validationError)
									: formatMessage(messages.fetchError)
							}
							isOpen={isInlineDialogOpen}
							placement="right"
							messageId="label-picker.inline-dialog"
							messageType="transactional"
						>
							<CreatableSelect
								{...rest}
								inputId={fieldProps.id}
								isClearable={false}
								defaultOptions
								autoFocus={!!this.props.autoFocus}
								// @ts-expect-error - TS2322 - Type '(selection: LabelOption[] | LabelOption | null, actionMeta: {    action: string;}, analyticsEvent?: any) => void' is not assignable to type '(value: unknown, action: ActionMeta<unknown>) => void'.
								onChange={this.onSelected}
								noOptionsMessage={this.noOptionsMessage}
								loadingMessage={this.loadingMessage}
								validationState={validationState}
								// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
								menuPortalTarget={noPortal ? null : document.body}
								value={pickerValues}
								onCreateOption={this.onCreateOption}
								formatCreateLabel={this.formatCreateLabel}
								{...(!expVal(
									'adding-color-to-labels-field-experiment-milestone2',
									'isColorfulNewLabelsEnabled',
									false,
								) && isColored
									? { formatOptionLabel: this.formatOptionLabel }
									: {})}
								allowCreateWhileLoading
								isMulti
								options={options}
								onInputChange={this.onInputChange}
								isLoading={isLoading}
								// @ts-expect-error - ts2322 -  Type 'ClearIndicatorProps<unknown, boolean, GroupBase<unknown>>' is not assignable to type 'ClearIndicatorProps<LabelOption, true, GroupBase<LabelOption>>'.
								styles={styles()}
								// @ts-expect-error - ts2322 - Type 'MultiValueRemoveProps<unknown, boolean, GroupBase<unknown>>' is not assignable to type 'MultiValueRemoveProps<LabelOption, boolean, GroupBase<LabelOption>>'.
								components={
									expVal(
										'adding-color-to-labels-field-experiment-milestone2',
										'isColorfulNewLabelsEnabled',
										false,
									)
										? {
												...styleComponents(),
												Option: (optionProps: OptionProps<LabelOption>) => {
													return (
														<JiraColoredLabel
															optionProps={optionProps}
															createNewLabelString={`(${formatMessage(messages.new)})`}
															onEditOption={this.onEditOption}
															inputRef={this.inputRef}
															lastFocusedKey={this.props.lastFocusedKey}
															isFocusingOptions={this.state.isFocusingOptions}
															isLoading={this.state.isLoading}
															isProjectAdmin={this.props.isProjectAdmin}
															isGlobalAdmin={this.props.isGlobalAdmin}
														/>
													);
												},
											}
										: styleComponents()
								}
								{...(expVal(
									'adding-color-to-labels-field-experiment-milestone2',
									'isColorfulNewLabelsEnabled',
									false,
								)
									? {
											UNSAFE_is_experimental_generic: true,
											onKeyDown: this.handleKeyDown,
											ref: this.inputRef,
											menuIsOpen: this.state.isMenuOpen && !showColorPickerPopupWhenEdited,
											onFocus: this.handleFocus,
											onBlur: this.handleBlur,
											isDisabled: this.props.isDisabled || showColorPickerPopupWhenEdited,
										}
									: {})}
							/>
						</InlineDialog>
					</>
				)}
			</Field>
		);

		const handleTrigger = (triggerProps: TriggerProps) => (
			<span {...triggerProps} role="menu">
				{labelsField}
			</span>
		);

		return showColorPickerPopupWhenEdited &&
			expVal(
				'adding-color-to-labels-field-experiment-milestone2',
				'isColorfulNewLabelsEnabled',
				false,
			) ? (
			<Popup
				zIndex={themeLayers.modal()}
				isOpen={showColorPickerPopupWhenEdited}
				autoFocus={false}
				placement="bottom-start"
				fallbackPlacements={['bottom-end']}
				trigger={handleTrigger}
				popupComponent={PopupContainer}
				onClose={this.resetLabelsColorPickerState}
				content={popupContent}
				messageId="label-picker.popup.jira-label-picker.ui.pickers.label.popup"
				messageType="transactional"
			/>
		) : (
			labelsField
		);
	}
}

type ExtendedPopupComponentProps = PopupComponentProps & {
	onClick?: (e: React.MouseEvent) => void;
};
const PopupContainer = forwardRef<HTMLDivElement, ExtendedPopupComponentProps>((props, ref) => (
	<div {...props} ref={ref} />
));

export default injectIntl(LabelsPicker);
